import { useState, useEffect, useCallback, useRef } from "react";
import {
  Shield, Plus, Search, Eye, EyeOff, Copy, ExternalLink, X,
  Wifi, Users, Monitor, Lock, Tag, Loader2, Trash2, Edit3,
  ChevronRight, Check, Building2, AlertTriangle, Share2,
  Clock, QrCode, ClipboardCopy, LogIn, Settings, Key, Globe,
  Server, Database, Home, Phone, Mail, Cloud, File, Folder,
  Star, Heart, Zap, Wrench, Cpu, HardDrive, Printer, Camera,
  Bluetooth, Headphones, MoreVertical, ArrowRight, Palette,
  ArrowLeft
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "../App";
import VaultPinGate from "../components/vault/VaultPinGate";
import VaultShareTokensSheet from "../components/vault/VaultShareTokensSheet";
import { DndContext, PointerSensor, TouchSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { GripVertical } from "lucide-react";

const API = "/api";

function authFetch(url, opts = {}) {
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}`, ...(opts.headers || {}) };
  if (!(opts.body instanceof FormData)) headers["Content-Type"] = "application/json";
  // Attach vault PIN token automatically for all vault-related requests
  const pinToken = sessionStorage.getItem("tgaone.vault.pinToken");
  if (pinToken && url.includes("/vault")) headers["X-Vault-PIN-Token"] = pinToken;
  return fetch(url, { ...opts, headers });
}

// ─── Icon mapping ───
const ICON_MAP = {
  wifi: Wifi, users: Users, monitor: Monitor, lock: Lock, tag: Tag, key: Key,
  globe: Globe, server: Server, database: Database, shield: Shield,
  settings: Settings, home: Home, building: Building2, phone: Phone,
  mail: Mail, cloud: Cloud, file: File, folder: Folder, star: Star,
  heart: Heart, zap: Zap, tool: Wrench, cpu: Cpu, "hard-drive": HardDrive,
  printer: Printer, camera: Camera, bluetooth: Bluetooth, headphones: Headphones,
};

const ICON_LIST = Object.keys(ICON_MAP);

const COLOR_LIST = [
  "blue", "emerald", "amber", "purple", "gray", "red", "orange", "cyan",
  "pink", "indigo", "teal", "sky", "violet", "fuchsia", "lime", "rose",
];

const ACCESS_OPTIONS = [
  { id: "admin_only", label: "Nur Admin" },
  { id: "project_team", label: "Projektteam" },
  { id: "specific_users", label: "Bestimmte Mitarbeiter" },
  { id: "all", label: "Alle Mitarbeiter" },
];

function getIconComponent(iconName) {
  return ICON_MAP[iconName] || Tag;
}

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} kopiert`);
  } catch {
    toast.error("Kopieren fehlgeschlagen");
  }
}


// ═══════════════════════════════════════════
// DnD Components for Vault Entries
// ═══════════════════════════════════════════
function DraggableEntry({ entry, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: entry.id,
    data: { entry },
  });
  const style = {
    transform: transform ? `translate(${transform.x}px, ${transform.y}px)` : undefined,
    opacity: isDragging ? 0.4 : 1,
    transition: isDragging ? "none" : "opacity 200ms ease",
    position: "relative",
    zIndex: isDragging ? 50 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="absolute left-0 top-0 bottom-0 flex items-center pl-1 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity" {...listeners} data-testid={`drag-handle-${entry.id}`}>
        <GripVertical className="w-3.5 h-3.5 text-gray-600" />
      </div>
      {children}
    </div>
  );
}

function DroppableCategoryZone({ slug, children, isOver }) {
  const { setNodeRef } = useDroppable({ id: `cat-${slug}` });
  return (
    <div ref={setNodeRef} className="relative" data-testid={`drop-zone-${slug}`}>
      {isOver && (
        <div className="absolute inset-0 rounded-xl border-2 border-dashed border-blue-500/50 bg-blue-500/5 z-10 pointer-events-none" />
      )}
      {children}
    </div>
  );
}


// ═══════════════════════════════════════════
// MAIN: Password Vault Page
// ═══════════════════════════════════════════
export default function PasswordVault({ isMobile }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [detailEntry, setDetailEntry] = useState(null);
  const [presetProject, setPresetProject] = useState(null);
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [moveEntry, setMoveEntry] = useState(null);
  const [dragActiveId, setDragActiveId] = useState(null);
  const [overCatSlug, setOverCatSlug] = useState(null);
  const [showShareTokens, setShowShareTokens] = useState(false);
  const [activeTokenCount, setActiveTokenCount] = useState(0);

  /* ── DnD Sensors ── */
  const pointerSensor = useSensor(PointerSensor, { activationConstraint: { distance: 8 } });
  const touchSensor = useSensor(TouchSensor, { activationConstraint: { delay: 300, tolerance: 5 } });
  const sensors = useSensors(pointerSensor, touchSensor);

  const handleDragStart = (event) => { setDragActiveId(event.active.id); };
  const handleDragOver = (event) => {
    const overId = event.over?.id;
    if (overId && String(overId).startsWith("cat-")) {
      setOverCatSlug(String(overId).replace("cat-", ""));
    } else {
      setOverCatSlug(null);
    }
  };
  const handleDragEnd = async (event) => {
    setDragActiveId(null);
    setOverCatSlug(null);
    const { active, over } = event;
    if (!over || !String(over.id).startsWith("cat-")) return;
    const targetSlug = String(over.id).replace("cat-", "");
    const draggedEntry = active.data?.current?.entry;
    if (!draggedEntry || draggedEntry.category === targetSlug) return;
    try {
      const res = await authFetch(`${API}/vault/${draggedEntry.id}/move`, {
        method: "PATCH", body: JSON.stringify({ category: targetSlug }),
      });
      if (res.ok) {
        toast.success(`"${draggedEntry.title}" verschoben`);
        reload();
      } else { toast.error("Verschieben fehlgeschlagen"); }
    } catch { toast.error("Netzwerkfehler"); }
  };
  const handleDragCancel = () => { setDragActiveId(null); setOverCatSlug(null); };
  const draggedEntry = entries.find(e => e.id === dragActiveId);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("create") === "true" && isAdmin) {
      const pid = params.get("projectId");
      const pname = params.get("projectName");
      if (pid) setPresetProject({ id: pid, name: pname || "" });
      setShowCreate(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [isAdmin]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/vault/categories`);
      if (res.ok) setCategories(await res.json());
    } catch {}
  }, []);

  const loadEntries = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/vault`);
      if (res.ok) setEntries(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadCategories(); loadEntries(); }, [loadCategories, loadEntries]);

  // Active share-tokens count (admin only) — refreshed every 60s + when sheet closes
  const loadTokenCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await authFetch(`${API}/vault/share-tokens`);
      if (res.ok) {
        const d = await res.json();
        setActiveTokenCount(d.count || 0);
      }
    } catch {}
  }, [isAdmin]);
  useEffect(() => {
    loadTokenCount();
    const t = setInterval(loadTokenCount, 60_000);
    return () => clearInterval(t);
  }, [loadTokenCount]);

  const reload = () => { loadCategories(); loadEntries(); };

  const filtered = entries.filter(e => {
    if (filterCat && e.category !== filterCat) return false;
    if (search) {
      const s = search.toLowerCase();
      return (e.title || "").toLowerCase().includes(s) ||
        (e.username || "").toLowerCase().includes(s) ||
        (e.project_name || "").toLowerCase().includes(s);
    }
    return true;
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Eintrag wirklich loeschen?")) return;
    const res = await authFetch(`${API}/vault/${id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Geloescht"); reload(); setDetailEntry(null); }
    else toast.error("Fehler beim Loeschen");
  };

  const getCatConfig = (slug) => categories.find(c => c.slug === slug) || { name: slug, icon: "tag", color: "gray" };

  // Long press for mobile move
  const handleLongPress = (entry) => {
    if (isAdmin) setMoveEntry(entry);
  };

  if (detailEntry) {
    return (
      <>
        <VaultDetail
          entry={detailEntry}
          categories={categories}
          isAdmin={isAdmin}
          getCatConfig={getCatConfig}
          onBack={() => { setDetailEntry(null); reload(); }}
          onEdit={() => { setEditEntry(detailEntry); setDetailEntry(null); setShowCreate(true); }}
          onDelete={() => handleDelete(detailEntry.id)}
          onMoveCategory={(entry) => setMoveEntry(entry)}
        />
        {/* Move Category Sheet - rendered outside detail view to ensure visibility */}
        {moveEntry && (
          <MoveCategorySheet
            entry={moveEntry}
            categories={categories}
            onClose={() => { setMoveEntry(null); setDetailEntry(null); reload(); }}
          />
        )}
      </>
    );
  }

  // ── Strict Admin-Only (Security-Policy) ──
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-8" data-testid="vault-access-denied">
        <div className="max-w-md rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/15 flex items-center justify-center">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-bold mb-2 text-white">Kein Zugriff</h2>
          <p className="text-sm text-gray-400">
            Der Passwort-Tresor ist ausschliesslich fuer Administratoren sichtbar.
          </p>
        </div>
      </div>
    );
  }

  return (
    <VaultPinGate onUnlocked={reload}>
    <div className="bg-[#0f1117] text-white min-h-full pb-24" data-testid="vault-page">
      {/* Header */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-amber-500/15 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Passwort-Tresor</h1>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">{entries.length} Eintraege</p>
            </div>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <>
                {/* Aktive QR-Codes Overview (Badge zeigt Anzahl) */}
                <button onClick={() => setShowShareTokens(true)}
                  className="relative w-10 h-10 bg-white/[0.06] border border-white/10 rounded-xl flex items-center justify-center active:bg-white/10 transition-colors"
                  data-testid="vault-tokens-btn"
                  title="Aktive QR-Codes anzeigen"
                >
                  <QrCode className="w-4.5 h-4.5 text-amber-400" />
                  {activeTokenCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] font-bold text-black flex items-center justify-center"
                      data-testid="vault-tokens-badge">
                      {activeTokenCount > 99 ? "99+" : activeTokenCount}
                    </span>
                  )}
                </button>
                <button onClick={() => setShowCatMgr(true)}
                  className="w-10 h-10 bg-white/[0.06] border border-white/10 rounded-xl flex items-center justify-center active:bg-white/10 transition-colors"
                  data-testid="vault-manage-cats-btn"
                  title="Kategorien verwalten"
                >
                  <Settings className="w-4.5 h-4.5 text-gray-400" />
                </button>
                <button onClick={() => { setEditEntry(null); setShowCreate(true); }}
                  className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center active:scale-95 transition-transform"
                  data-testid="vault-add-btn"
                >
                  <Plus className="w-5 h-5 text-white" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Suchen..."
            className="w-full h-10 pl-10 pr-4 bg-white/[0.06] border border-white/10 rounded-xl text-sm text-white placeholder:text-gray-600 outline-none focus:border-blue-500/50"
            data-testid="vault-search"
          />
        </div>
      </div>

      {/* DnD Context wraps categories (droppable) + entries (draggable) */}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>

      {/* Category Filter with Counts – also droppable zones */}
      <div className="px-4 pb-2">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
          <button
            onClick={() => setFilterCat("")}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${!filterCat ? "bg-white/15 text-white" : "bg-white/[0.04] text-gray-500 active:bg-white/8"}`}
            data-testid="vault-filter-all"
          >Alle ({entries.length})</button>
          {categories.map(cat => {
            const CIcon = getIconComponent(cat.icon);
            const isDropTarget = overCatSlug === cat.slug && dragActiveId;
            return (
              <DroppableCategoryZone key={cat.slug} slug={cat.slug} isOver={isDropTarget}>
                <button
                  onClick={() => setFilterCat(filterCat === cat.slug ? "" : cat.slug)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-1.5 transition-all duration-150 ${filterCat === cat.slug ? "bg-white/15 text-white" : "bg-white/[0.04] text-gray-500 active:bg-white/8"} ${isDropTarget ? "ring-2 ring-blue-500/60 bg-blue-500/10 scale-105" : ""}`}
                  data-testid={`vault-filter-${cat.slug}`}
                >
                  <CIcon className="w-3 h-3" />{cat.name} ({cat.entry_count || 0})
                </button>
              </DroppableCategoryZone>
            );
          })}
        </div>
      </div>

      {/* Entries List */}
      <div className="px-4 space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <Shield className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <p className="text-sm text-gray-500">{search ? "Keine Ergebnisse" : "Noch keine Eintraege"}</p>
          </div>
        ) : (
          filtered.map(entry => {
            const catConf = getCatConfig(entry.category);
            const CatIcon = getIconComponent(catConf.icon);
            let longTimer = null;
            return isAdmin ? (
              <DraggableEntry key={entry.id} entry={entry}>
                <button
                  onClick={() => setDetailEntry(entry)}
                  className="group w-full flex items-center gap-3 bg-white/[0.04] border border-white/8 rounded-xl px-4 pl-7 py-3.5 active:bg-white/[0.08] transition-colors text-left"
                  data-testid={`vault-entry-${entry.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-${catConf.color}-500/15`}>
                    <CatIcon className={`w-5 h-5 text-${catConf.color}-400`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{entry.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {entry.username && <p className="text-xs text-gray-500 truncate">{entry.username}</p>}
                      {entry.project_name && (
                        <span className="text-[10px] text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded font-medium truncate">{entry.project_name}</span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
                </button>
              </DraggableEntry>
            ) : (
              <button key={entry.id}
                onClick={() => setDetailEntry(entry)}
                onTouchStart={() => { longTimer = setTimeout(() => handleLongPress(entry), 600); }}
                onTouchEnd={() => clearTimeout(longTimer)}
                onTouchMove={() => clearTimeout(longTimer)}
                className="w-full flex items-center gap-3 bg-white/[0.04] border border-white/8 rounded-xl px-4 py-3.5 active:bg-white/[0.08] transition-colors text-left"
                data-testid={`vault-entry-${entry.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-${catConf.color}-500/15`}>
                  <CatIcon className={`w-5 h-5 text-${catConf.color}-400`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{entry.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {entry.username && <p className="text-xs text-gray-500 truncate">{entry.username}</p>}
                    {entry.project_name && (
                      <span className="text-[10px] text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded font-medium truncate">{entry.project_name}</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-600 flex-shrink-0" />
              </button>
            );
          })
        )}
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {draggedEntry ? (
          <div className="bg-[#1e293b] border border-blue-500/30 rounded-xl px-4 py-3.5 shadow-2xl flex items-center gap-3 opacity-90 max-w-[300px]">
            <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0">
              <GripVertical className="w-4 h-4 text-blue-400" />
            </div>
            <p className="text-sm font-semibold text-white truncate">{draggedEntry.title}</p>
          </div>
        ) : null}
      </DragOverlay>
      </DndContext>

      {/* Create/Edit Sheet */}
      {showCreate && (
        <VaultForm
          editEntry={editEntry}
          presetProject={presetProject}
          categories={categories}
          onClose={() => { setShowCreate(false); setEditEntry(null); setPresetProject(null); }}
          onSaved={() => { setShowCreate(false); setEditEntry(null); setPresetProject(null); reload(); }}
        />
      )}

      {/* Category Manager */}
      {showCatMgr && (
        <CategoryManager
          categories={categories}
          onClose={() => { setShowCatMgr(false); reload(); }}
          onReload={reload}
        />
      )}

      {/* Move Category Sheet */}
      {moveEntry && (
        <MoveCategorySheet
          entry={moveEntry}
          categories={categories}
          onClose={() => { setMoveEntry(null); reload(); }}
        />
      )}

      {/* Active QR-Code Tokens — Admin Overview */}
      {showShareTokens && (
        <VaultShareTokensSheet
          onClose={() => { setShowShareTokens(false); loadTokenCount(); }}
        />
      )}
    </div>
    </VaultPinGate>
  );
}


// ═══════════════════════════════════════════
// DETAIL VIEW
// ═══════════════════════════════════════════
function VaultDetail({ entry, categories, isAdmin, getCatConfig, onBack, onEdit, onDelete, onMoveCategory }) {
  const [password, setPassword] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [shareMinutes, setShareMinutes] = useState(15);
  const [customHours, setCustomHours] = useState("");
  const [customMinutes, setCustomMinutes] = useState("");

  /* ── Swipe-to-Back ── */
  const [swipeX, setSwipeX] = useState(0);
  const touchRef = useRef({ startX: 0, startY: 0, tracking: false });
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    const t = e.touches[0];
    // Nur starten wenn Touch am linken Drittel beginnt (natuerliches Edge-Swipe)
    if (t.clientX < window.innerWidth * 0.35) {
      touchRef.current = { startX: t.clientX, startY: t.clientY, tracking: true };
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchRef.current.tracking) return;
    const t = e.touches[0];
    const dx = t.clientX - touchRef.current.startX;
    const dy = Math.abs(t.clientY - touchRef.current.startY);
    // Abbrechen wenn vertikales Scrollen dominiert
    if (dy > 40 && dx < dy) { touchRef.current.tracking = false; setSwipeX(0); return; }
    if (dx > 10) setSwipeX(Math.min(dx, 160));
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchRef.current.tracking) return;
    touchRef.current.tracking = false;
    if (swipeX > 80) { onBack(); }
    setSwipeX(0);
  }, [swipeX, onBack]);

  const catConf = getCatConfig(entry.category);
  const CatIcon = getIconComponent(catConf.icon);

  const revealPassword = async () => {
    setRevealing(true);
    try {
      const res = await authFetch(`${API}/vault/${entry.id}/reveal`, { method: "POST" });
      if (res.ok) { const data = await res.json(); setPassword(data.password); setShowPw(true); }
      else toast.error("Zugriff verweigert");
    } catch { toast.error("Fehler"); }
    setRevealing(false);
  };

  const loadAudit = async () => {
    const res = await authFetch(`${API}/vault/${entry.id}/audit`);
    if (res.ok) setAuditLog(await res.json());
    setShowAudit(true);
  };

  const quickCopy = async () => {
    try {
      const res = await authFetch(`${API}/vault/${entry.id}/reveal`, { method: "POST" });
      if (!res.ok) { toast.error("Zugriff verweigert"); return; }
      const data = await res.json();
      await navigator.clipboard.writeText([data.username, data.password].filter(Boolean).join("\n"));
      toast.success("Zugangsdaten kopiert");
    } catch { toast.error("Fehler beim Kopieren"); }
  };

  const openAndCopy = async () => { await quickCopy(); if (entry.url) window.open(entry.url, "_blank"); };

  const MAX_MINUTES = 10080; // 7 Tage

  // Berechnete Gesamtdauer: custom (falls eingegeben) ueberschreibt Buttons
  const customTotal = (parseInt(customHours, 10) || 0) * 60 + (parseInt(customMinutes, 10) || 0);
  const effectiveMinutes = customTotal > 0 ? customTotal : shareMinutes;
  const customError = customTotal > 0 && (customTotal < 1 || customTotal > MAX_MINUTES)
    ? (customTotal > MAX_MINUTES ? `Max. 7 Tage (${MAX_MINUTES} Min.)` : "Mindestens 1 Minute")
    : null;

  const formatDuration = (mins) => {
    if (mins < 60) return `${mins} Minuten`;
    if (mins < 1440) {
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m > 0 ? `${h} Std ${m} Min` : `${h} Stunden`;
    }
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    return h > 0 ? `${d} Tage ${h} Std` : `${d} Tage`;
  };

  const createShareLink = async () => {
    if (customError) { toast.error(customError); return; }
    setSharingLoading(true);
    try {
      const res = await authFetch(`${API}/vault/${entry.id}/share`, {
        method: "POST", body: JSON.stringify({ expires_minutes: effectiveMinutes }),
      });
      if (res.ok) {
        const data = await res.json();
        setShareData({ ...data, url: `${window.location.origin}/shared/vault/${data.token}` });
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler beim Erstellen");
      }
    } catch { toast.error("Netzwerkfehler"); }
    setSharingLoading(false);
  };

  return (
    <div
      ref={containerRef}
      className="bg-[#0f1117] text-white min-h-full pb-24 px-4 pt-4"
      data-testid="vault-detail"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: swipeX > 0 ? `translateX(${swipeX}px)` : undefined,
        opacity: swipeX > 0 ? 1 - swipeX / 320 : 1,
        transition: swipeX > 0 ? "none" : "transform 250ms ease, opacity 250ms ease",
      }}
    >
      {/* Swipe-Indikator */}
      {swipeX > 20 && (
        <div
          className="fixed left-2 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full z-50 pointer-events-none"
          style={{
            width: 36,
            height: 36,
            background: swipeX > 80 ? "rgba(59,130,246,0.3)" : "var(--tga-border-strong)",
            transition: "background 100ms ease",
          }}
        >
          <ArrowLeft className="w-4 h-4" style={{ color: swipeX > 80 ? "#60a5fa" : "var(--tga-text-muted)" }} />
        </div>
      )}
      {/* ── Zurueck-Button + Breadcrumb ── */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-150 min-h-[44px] active:scale-[0.97]"
          style={{
            color: "var(--tga-text-secondary)",
            background: "var(--tga-border)",
            border: "1px solid var(--tga-border)",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "var(--tga-text-primary)"; e.currentTarget.style.background = "var(--tga-border-strong)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-text-secondary)"; e.currentTarget.style.background = "var(--tga-border)"; }}
          data-testid="vault-detail-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurueck
        </button>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--tga-text-muted)" }}>
          <Shield className="w-3.5 h-3.5" />
          <span>Passwort-Tresor</span>
          <ChevronRight className="w-3 h-3" />
          <span className="truncate max-w-[180px]" style={{ color: "var(--tga-text-secondary)" }}>{entry.title}</span>
        </div>
      </div>

      <div className="flex items-start gap-3 mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-${catConf.color}-500/15`}>
          <CatIcon className={`w-6 h-6 text-${catConf.color}-400`} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-white">{entry.title}</h1>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-gray-500">{catConf.name}</span>
            {entry.project_name && (
              <span className="text-[10px] text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded font-medium">{entry.project_name}</span>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2 mb-4">
        <button onClick={quickCopy}
          className="flex-1 h-11 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-emerald-500/20 transition-colors"
          data-testid="vault-quick-copy"
        ><ClipboardCopy className="w-3.5 h-3.5" />Kopieren</button>
        {entry.url && (
          <button onClick={openAndCopy}
            className="flex-1 h-11 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-blue-500/20 transition-colors"
            data-testid="vault-open-copy"
          ><LogIn className="w-3.5 h-3.5" />Oeffnen & Kopieren</button>
        )}
        {isAdmin && (
          <button onClick={() => setShowShare(!showShare)}
            className="h-11 px-4 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:bg-amber-500/20 transition-colors"
            data-testid="vault-share-btn"
          ><QrCode className="w-3.5 h-3.5" />Teilen</button>
        )}
      </div>

      {/* QR Share Panel */}
      {showShare && isAdmin && (
        <div className="bg-white/[0.04] border border-amber-500/20 rounded-xl p-4 mb-4" data-testid="vault-share-panel">
          {!shareData ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-white flex items-center gap-2"><Share2 className="w-4 h-4 text-amber-400" />Temporaerer QR-Code</p>
              <p className="text-xs text-gray-400">Zugangsdaten zeitlich begrenzt per QR-Code teilen.</p>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Schnellauswahl:</span>
                {[5, 15, 30, 60].map(m => {
                  const active = customTotal === 0 && shareMinutes === m;
                  return (
                    <button key={m} onClick={() => { setShareMinutes(m); setCustomHours(""); setCustomMinutes(""); }}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${active ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-white/[0.04] text-gray-500 border border-transparent"}`}
                      data-testid={`vault-share-${m}min`}
                    >{m} Min</button>
                  );
                })}
              </div>

              {/* Eigene Dauer */}
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid="vault-share-custom">
                <p className="text-[11px] uppercase tracking-wider text-gray-400 font-bold">Eigene Dauer</p>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-500 block mb-1">Stunden</label>
                    <input type="number" min={0} max={168} placeholder="0"
                      value={customHours} onChange={e => setCustomHours(e.target.value.replace(/[^0-9]/g, ""))}
                      className="w-full h-9 text-sm rounded-md bg-white/[0.04] border border-white/10 px-2 text-white text-center focus:outline-none focus:border-amber-500/40"
                      data-testid="vault-share-custom-hours" />
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-500 block mb-1">Minuten</label>
                    <input type="number" min={0} max={59} placeholder="0"
                      value={customMinutes} onChange={e => setCustomMinutes(e.target.value.replace(/[^0-9]/g, ""))}
                      className="w-full h-9 text-sm rounded-md bg-white/[0.04] border border-white/10 px-2 text-white text-center focus:outline-none focus:border-amber-500/40"
                      data-testid="vault-share-custom-minutes" />
                  </div>
                  {customTotal > 0 && (
                    <button onClick={() => { setCustomHours(""); setCustomMinutes(""); }}
                      className="h-9 px-2 rounded-md text-[11px] text-gray-400 hover:text-white border border-white/10 bg-white/[0.04]"
                      title="Eigene Dauer zuruecksetzen"
                      data-testid="vault-share-custom-clear">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {customError && (
                  <p className="text-[11px] text-red-400" data-testid="vault-share-custom-error">{customError}</p>
                )}
                <p className="text-[10px] text-gray-500">Maximum: 7 Tage. Eigene Dauer ueberschreibt Schnellauswahl.</p>
              </div>

              {/* Live-Anzeige der gewaehlten Dauer */}
              <p className="text-xs text-amber-300 flex items-center gap-1" data-testid="vault-share-effective">
                <Clock className="w-3 h-3" />
                Gueltig fuer: <span className="font-bold">{formatDuration(effectiveMinutes)}</span>
              </p>

              <button onClick={createShareLink} disabled={sharingLoading || !!customError}
                className="w-full h-10 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
                data-testid="vault-share-create"
              >
                {sharingLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                QR-Code erstellen
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="vault-share-qr">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">QR-Code bereit</p>
                <button onClick={() => { setShareData(null); setShowShare(false); }} className="text-gray-500 active:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex justify-center py-3 bg-white rounded-xl">
                <QRCodeSVG value={shareData.url} size={180} level="M" />
              </div>
              <p className="text-center text-[10px] text-gray-500 flex items-center justify-center gap-1">
                <Clock className="w-3 h-3" />Gueltig bis {new Date(shareData.expires_at).toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr
              </p>
              <button onClick={() => copyToClipboard(shareData.url, "Share-Link")}
                className="w-full h-9 bg-white/[0.06] border border-white/10 text-gray-300 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 active:bg-white/10"
                data-testid="vault-share-copy-link"
              ><Copy className="w-3.5 h-3.5" />Link kopieren</button>
            </div>
          )}
        </div>
      )}

      {/* Fields */}
      <div className="space-y-3">
        {entry.username && (
          <div className="bg-white/[0.04] border border-white/8 rounded-xl p-4 flex items-center justify-between" data-testid="vault-detail-username">
            <div><p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Benutzername</p><p className="text-sm text-white font-mono">{entry.username}</p></div>
            <button onClick={() => copyToClipboard(entry.username, "Benutzername")} className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors" data-testid="vault-copy-username"><Copy className="w-4 h-4 text-gray-400" /></button>
          </div>
        )}

        <div className="bg-white/[0.04] border border-white/8 rounded-xl p-4" data-testid="vault-detail-password">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Passwort</p>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-white font-mono flex-1 truncate">{showPw && password ? password : "••••••••••••"}</p>
            <div className="flex gap-1.5">
              <button onClick={() => { if (showPw) setShowPw(false); else revealPassword(); }} disabled={revealing}
                className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors" data-testid="vault-toggle-password"
              >{revealing ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> : showPw ? <EyeOff className="w-4 h-4 text-gray-400" /> : <Eye className="w-4 h-4 text-gray-400" />}</button>
              {showPw && password && (
                <button onClick={() => copyToClipboard(password, "Passwort")} className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors" data-testid="vault-copy-password"><Copy className="w-4 h-4 text-gray-400" /></button>
              )}
            </div>
          </div>
        </div>

        {/* E-Mail Konto Details — angezeigt fuer entry_type == 'email_account' (Daten sind bereits vorhanden) */}
        {entry.entry_type === "email_account" && (
          <EmailAccountDetailsBlock
            entry={entry}
            password={password}
            onRequirePassword={revealPassword}
            revealing={revealing}
          />
        )}

        {entry.url && (
          <div className="bg-white/[0.04] border border-white/8 rounded-xl p-4 flex items-center justify-between" data-testid="vault-detail-url">
            <div className="min-w-0 flex-1"><p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">URL</p><p className="text-sm text-blue-400 truncate">{entry.url}</p></div>
            <a href={entry.url} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-white/[0.06] rounded-lg active:bg-white/15 transition-colors ml-2" data-testid="vault-open-url"><ExternalLink className="w-4 h-4 text-blue-400" /></a>
          </div>
        )}

        {entry.description && (
          <div className="bg-white/[0.04] border border-white/8 rounded-xl p-4" data-testid="vault-detail-desc">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Beschreibung</p>
            <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{entry.description}</p>
          </div>
        )}

        <div className="bg-white/[0.04] border border-white/8 rounded-xl p-4">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Details</p>
          <div className="space-y-1.5 text-xs text-gray-400">
            <p>Zugriff: <span className="text-white font-medium">{ACCESS_OPTIONS.find(a => a.id === entry.access_type)?.label || entry.access_type}</span></p>
            {entry.created_by_name && <p>Erstellt von: <span className="text-white">{entry.created_by_name}</span></p>}
            {entry.created_at && <p>Erstellt: <span className="text-white">{new Date(entry.created_at).toLocaleString("de-DE")}</span></p>}
          </div>
        </div>

        {isAdmin && (
          <div className="space-y-2 pt-2">
            <div className="flex gap-2">
              <button onClick={onEdit} className="flex-1 h-11 bg-white/[0.06] border border-white/10 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:bg-white/10" data-testid="vault-edit-btn"><Edit3 className="w-4 h-4" />Bearbeiten</button>
              <button onClick={() => onMoveCategory(entry)} className="h-11 px-4 bg-white/[0.06] border border-white/10 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:bg-white/10" data-testid="vault-move-btn"><ArrowRight className="w-4 h-4" />Verschieben</button>
              <button onClick={onDelete} className="h-11 px-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 active:bg-red-500/20" data-testid="vault-delete-btn"><Trash2 className="w-4 h-4" /></button>
            </div>
            <button onClick={loadAudit} className="w-full h-10 bg-white/[0.04] border border-white/8 text-gray-400 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 active:bg-white/8" data-testid="vault-audit-btn"><AlertTriangle className="w-3.5 h-3.5" />Zugriffs-Log</button>
          </div>
        )}

        {showAudit && auditLog.length > 0 && (
          <div className="bg-white/[0.04] border border-white/8 rounded-xl p-4" data-testid="vault-audit-log">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Zugriffs-Log</p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {auditLog.map((log, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    log.action === "password_revealed" ? "bg-amber-500/15 text-amber-400" :
                    log.action === "created" ? "bg-emerald-500/15 text-emerald-400" :
                    log.action === "updated" ? "bg-blue-500/15 text-blue-400" :
                    log.action === "shared" ? "bg-purple-500/15 text-purple-400" :
                    "bg-red-500/15 text-red-400"
                  }`}>
                    {log.action === "password_revealed" ? "ANGESEHEN" : log.action === "created" ? "ERSTELLT" :
                     log.action === "updated" ? "BEARBEITET" : log.action === "shared" ? "GETEILT" : "GELOESCHT"}
                  </span>
                  <span className="text-gray-400">{log.user_name}</span>
                  <span className="text-gray-600 ml-auto">{new Date(log.timestamp).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════
// EMAIL ACCOUNT DETAILS BLOCK (Display-only)
// Shows IMAP / SMTP server, port, security in human-friendly form
// with per-field copy buttons + "Alle Daten kopieren" button.
// No business logic — purely renders existing entry fields.
// ═══════════════════════════════════════════
function EmailAccountDetailsBlock({ entry, password, onRequirePassword, revealing }) {
  const securityLabel = (s) => {
    const v = (s || "").toUpperCase();
    if (v === "SSL_TLS" || v === "SSL" || v === "TLS") return "SSL/TLS";
    if (v === "STARTTLS") return "STARTTLS";
    if (v === "NONE" || v === "PLAIN") return "Keine";
    return s || "—";
  };

  const rows = [
    { key: "email_address", label: "E-Mail Adresse", value: entry.email_address || entry.username, mono: true },
    { key: "imap_host", label: "IMAP Server", value: entry.imap_host, mono: true },
    { key: "imap_port", label: "IMAP Port", value: entry.imap_port ? String(entry.imap_port) : "", mono: true },
    { key: "imap_security", label: "IMAP Sicherheit", value: securityLabel(entry.imap_security), mono: false },
    { key: "smtp_host", label: "SMTP Server", value: entry.smtp_host, mono: true },
    { key: "smtp_port", label: "SMTP Port", value: entry.smtp_port ? String(entry.smtp_port) : "", mono: true },
    { key: "smtp_security", label: "SMTP Sicherheit", value: securityLabel(entry.smtp_security), mono: false },
  ].filter(r => !!r.value);

  const copyOne = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} kopiert`);
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const copyAll = async () => {
    // If password is not yet revealed, fetch it first so it can be included
    if (!password) {
      onRequirePassword?.();
      toast.info("Passwort wird geladen — bitte gleich nochmal auf 'Alle Daten kopieren' klicken");
      return;
    }
    const lines = [
      `Konto: ${entry.title}`,
      ...rows.map(r => `${r.label}: ${r.value}`),
      `Benutzername: ${entry.username || entry.email_address || ""}`,
      `Passwort: ${password}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Alle E-Mail-Daten kopiert");
    } catch {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  if (rows.length === 0) return null;

  return (
    <div className="bg-white/[0.04] border border-blue-500/20 rounded-xl p-4 space-y-2.5" data-testid="vault-detail-email-block">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider text-blue-300 font-bold flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />E-Mail Konto Details
        </p>
        <button
          onClick={copyAll}
          disabled={revealing}
          className="h-8 px-3 rounded-lg text-[11px] font-bold bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/30 text-blue-300 disabled:opacity-40 flex items-center gap-1.5"
          data-testid="vault-email-copy-all"
        >
          {revealing ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardCopy className="w-3 h-3" />}
          Alle Daten kopieren
        </button>
      </div>

      <div className="space-y-1.5">
        {rows.map(r => (
          <div
            key={r.key}
            className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white/[0.02] border border-white/5"
            data-testid={`vault-email-row-${r.key}`}
          >
            <div className="flex-1 min-w-0">
              <p className="text-[9px] uppercase tracking-wider text-gray-500 font-semibold leading-none mb-0.5">{r.label}</p>
              <p className={`text-[12px] text-white truncate ${r.mono ? "font-mono" : ""}`}>{r.value}</p>
            </div>
            <button
              onClick={() => copyOne(r.label, r.value)}
              className="p-1.5 bg-white/[0.06] rounded-md hover:bg-white/15 transition-colors flex-shrink-0"
              data-testid={`vault-email-copy-${r.key}`}
              title={`${r.label} kopieren`}
            >
              <Copy className="w-3.5 h-3.5 text-gray-400" />
            </button>
          </div>
        ))}
      </div>

      <p className="text-[10px] text-gray-500 pt-1">
        Tipp: „Alle Daten kopieren" enthaelt Benutzername & Passwort und kann direkt in Outlook eingefuegt werden.
      </p>
    </div>
  );
}



// ═══════════════════════════════════════════
// MOVE CATEGORY SHEET
// ═══════════════════════════════════════════
function MoveCategorySheet({ entry, categories, onClose }) {
  const [moving, setMoving] = useState(false);

  const moveToCategory = async (targetSlug) => {
    if (targetSlug === entry.category) { onClose(); return; }
    setMoving(true);
    try {
      const res = await authFetch(`${API}/vault/${entry.id}/move`, {
        method: "PATCH", body: JSON.stringify({ category: targetSlug }),
      });
      if (res.ok) { toast.success("Verschoben"); onClose(); }
      else toast.error("Fehler");
    } catch { toast.error("Netzwerkfehler"); }
    setMoving(false);
  };

  return (
    <div className="fixed inset-0 z-[850] flex items-end justify-center" data-testid="vault-move-sheet">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#1a1d27] border-t border-white/10 rounded-t-2xl pb-safe">
        <div className="px-5 py-4 border-b border-white/10">
          <h2 className="text-base font-bold text-white">Kategorie waehlen</h2>
          <p className="text-xs text-gray-500 mt-0.5">"{entry.title}" verschieben nach:</p>
        </div>
        <div className="px-5 py-3 space-y-1.5 max-h-[50vh] overflow-y-auto">
          {categories.map(cat => {
            const CIcon = getIconComponent(cat.icon);
            const isCurrent = cat.slug === entry.category;
            return (
              <button key={cat.slug}
                onClick={() => moveToCategory(cat.slug)}
                disabled={moving}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${isCurrent ? "bg-blue-500/15 border border-blue-500/30" : "bg-white/[0.04] border border-white/8 active:bg-white/10"}`}
                data-testid={`vault-move-to-${cat.slug}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${cat.color}-500/15`}>
                  <CIcon className={`w-4 h-4 text-${cat.color}-400`} />
                </div>
                <span className="text-sm font-medium text-white flex-1 text-left">{cat.name}</span>
                {isCurrent && <span className="text-[10px] text-blue-400 font-semibold">AKTUELL</span>}
                <span className="text-xs text-gray-600">{cat.entry_count || 0}</span>
              </button>
            );
          })}
        </div>
        <div className="px-5 py-3 border-t border-white/10">
          <button onClick={onClose} className="w-full h-10 bg-white/[0.06] border border-white/10 text-gray-400 rounded-xl text-sm font-semibold active:bg-white/10">Abbrechen</button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════
// CATEGORY MANAGER
// ═══════════════════════════════════════════
function CategoryManager({ categories, onClose, onReload }) {
  const [editCat, setEditCat] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [bulkMove, setBulkMove] = useState(null);

  const handleDelete = async (cat) => {
    if (cat.is_default) { toast.error("Standard-Kategorie kann nicht geloescht werden"); return; }
    if (cat.entry_count > 0) { setBulkMove(cat); return; }
    if (!window.confirm(`Kategorie "${cat.name}" wirklich loeschen?`)) return;
    const res = await authFetch(`${API}/vault/categories/${cat.id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Kategorie geloescht"); onReload(); }
    else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
  };

  return (
    <div className="fixed inset-0 z-[800] flex items-end justify-center" data-testid="vault-cat-manager">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#1a1d27] border-t border-white/10 rounded-t-2xl max-h-[85vh] overflow-y-auto pb-safe">
        <div className="sticky top-0 bg-[#1a1d27] z-10 px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-base font-bold text-white">Kategorien verwalten</h2>
          <div className="flex gap-2">
            <button onClick={() => { setEditCat(null); setShowCreate(true); }}
              className="px-3 py-1.5 bg-blue-600 rounded-lg text-xs font-semibold text-white flex items-center gap-1 active:bg-blue-500"
              data-testid="vault-cat-add-btn"
            ><Plus className="w-3 h-3" />Neue Kategorie</button>
            <button onClick={onClose} className="p-2 text-gray-400 active:text-white"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-2">
          {categories.map(cat => {
            const CIcon = getIconComponent(cat.icon);
            return (
              <div key={cat.id} className="flex items-center gap-3 bg-white/[0.04] border border-white/8 rounded-xl px-4 py-3" data-testid={`vault-cat-item-${cat.slug}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-${cat.color}-500/15`}>
                  <CIcon className={`w-5 h-5 text-${cat.color}-400`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-white">{cat.name}</p>
                    {cat.is_default && <span className="text-[10px] bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded font-bold">STANDARD</span>}
                  </div>
                  <p className="text-xs text-gray-500">{cat.entry_count || 0} Eintraege</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditCat(cat); setShowCreate(true); }}
                    className="p-2 bg-white/[0.06] rounded-lg active:bg-white/15" data-testid={`vault-cat-edit-${cat.slug}`}>
                    <Edit3 className="w-3.5 h-3.5 text-gray-400" />
                  </button>
                  {!cat.is_default && (
                    <button onClick={() => handleDelete(cat)}
                      className="p-2 bg-red-500/10 rounded-lg active:bg-red-500/20" data-testid={`vault-cat-delete-${cat.slug}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Create/Edit Category Form */}
      {showCreate && (
        <CategoryForm
          editCat={editCat}
          onClose={() => { setShowCreate(false); setEditCat(null); }}
          onSaved={() => { setShowCreate(false); setEditCat(null); onReload(); }}
        />
      )}

      {/* Bulk Move Dialog */}
      {bulkMove && (
        <BulkMoveDialog
          sourceCat={bulkMove}
          categories={categories}
          onClose={() => { setBulkMove(null); onReload(); }}
          onReload={onReload}
        />
      )}
    </div>
  );
}


// ═══════════════════════════════════════════
// CATEGORY FORM (Create/Edit)
// ═══════════════════════════════════════════
function CategoryForm({ editCat, onClose, onSaved }) {
  const [name, setName] = useState(editCat?.name || "");
  const [icon, setIcon] = useState(editCat?.icon || "tag");
  const [color, setColor] = useState(editCat?.color || "gray");
  const [isDefault, setIsDefault] = useState(editCat?.is_default || false);
  const [saving, setSaving] = useState(false);
  const [showIcons, setShowIcons] = useState(false);
  const isEdit = !!editCat;

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Name erforderlich"); return; }
    setSaving(true);
    const body = { name: name.trim(), icon, color, is_default: isDefault };
    try {
      const res = isEdit
        ? await authFetch(`${API}/vault/categories/${editCat.id}`, { method: "PUT", body: JSON.stringify(body) })
        : await authFetch(`${API}/vault/categories`, { method: "POST", body: JSON.stringify(body) });
      if (res.ok) { toast.success(isEdit ? "Gespeichert" : "Kategorie erstellt"); onSaved(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Netzwerkfehler"); }
    setSaving(false);
  };

  const SelectedIcon = getIconComponent(icon);

  return (
    <div className="fixed inset-0 z-[900] flex items-center justify-center px-4" data-testid="vault-cat-form">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-[#1a1d27] border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">{isEdit ? "Kategorie bearbeiten" : "Neue Kategorie"}</h3>
          <button onClick={onClose} className="text-gray-500 active:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="z.B. Alarmanlagen"
              className="w-full h-10 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50"
              data-testid="vault-cat-form-name"
            />
          </div>

          {/* Icon */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Icon</label>
            <button onClick={() => setShowIcons(!showIcons)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.06] border border-white/10 rounded-xl w-full text-left active:bg-white/10"
              data-testid="vault-cat-form-icon-btn"
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center bg-${color}-500/15`}>
                <SelectedIcon className={`w-4 h-4 text-${color}-400`} />
              </div>
              <span className="text-sm text-white flex-1">{icon}</span>
              <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${showIcons ? "rotate-90" : ""}`} />
            </button>
            {showIcons && (
              <div className="grid grid-cols-7 gap-1.5 mt-2 p-2 bg-white/[0.03] rounded-xl border border-white/8 max-h-40 overflow-y-auto">
                {ICON_LIST.map(ic => {
                  const IC = getIconComponent(ic);
                  return (
                    <button key={ic} onClick={() => { setIcon(ic); setShowIcons(false); }}
                      className={`p-2 rounded-lg flex items-center justify-center transition-colors ${icon === ic ? "bg-blue-500/20 border border-blue-500/30" : "bg-white/[0.04] active:bg-white/10"}`}
                      data-testid={`vault-cat-icon-${ic}`}
                    >
                      <IC className="w-4 h-4 text-gray-300" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Color */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Farbe</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_LIST.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg bg-${c}-500 transition-all ${color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#1a1d27] scale-110" : "opacity-60 hover:opacity-100"}`}
                  data-testid={`vault-cat-color-${c}`}
                />
              ))}
            </div>
          </div>

          {/* Default toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-white/10 text-blue-500 focus:ring-blue-500/20"
            />
            <span className="text-sm text-gray-300">Als Standard-Kategorie setzen</span>
          </label>

          <button onClick={handleSave} disabled={saving || !name.trim()}
            className="w-full h-11 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
            data-testid="vault-cat-form-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isEdit ? "Speichern" : "Erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════
// BULK MOVE DIALOG
// ═══════════════════════════════════════════
function BulkMoveDialog({ sourceCat, categories, onClose, onReload }) {
  const [targetSlug, setTargetSlug] = useState("");
  const [moving, setMoving] = useState(false);
  const targets = categories.filter(c => c.slug !== sourceCat.slug);

  const handleBulkMove = async () => {
    if (!targetSlug) { toast.error("Bitte Ziel-Kategorie waehlen"); return; }
    setMoving(true);
    try {
      const res = await authFetch(`${API}/vault/categories/${sourceCat.id}/bulk-move`, {
        method: "POST", body: JSON.stringify({ target_category_slug: targetSlug }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.moved} Eintraege verschoben`);
        // Now delete the empty category
        const delRes = await authFetch(`${API}/vault/categories/${sourceCat.id}`, { method: "DELETE" });
        if (delRes.ok) toast.success("Kategorie geloescht");
        onReload();
        onClose();
      } else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Netzwerkfehler"); }
    setMoving(false);
  };

  return (
    <div className="fixed inset-0 z-[950] flex items-center justify-center px-4" data-testid="vault-bulk-move">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-sm bg-[#1a1d27] border border-white/10 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <h3 className="text-sm font-bold text-white">Kategorie nicht leer</h3>
          </div>
          <p className="text-xs text-gray-400">
            "{sourceCat.name}" enthaelt noch <span className="text-white font-semibold">{sourceCat.entry_count}</span> Eintraege.
            Bitte zuerst alle Passwoerter in eine andere Kategorie verschieben.
          </p>
        </div>
        <div className="px-5 py-4 space-y-3">
          <label className="text-xs text-gray-400 font-semibold block">Alle verschieben nach:</label>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {targets.map(cat => {
              const CIcon = getIconComponent(cat.icon);
              return (
                <button key={cat.slug} onClick={() => setTargetSlug(cat.slug)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${targetSlug === cat.slug ? "bg-blue-500/15 border border-blue-500/30" : "bg-white/[0.04] border border-white/8 active:bg-white/10"}`}
                  data-testid={`vault-bulk-target-${cat.slug}`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${cat.color}-500/15`}>
                    <CIcon className={`w-4 h-4 text-${cat.color}-400`} />
                  </div>
                  <span className="text-sm text-white flex-1 text-left">{cat.name}</span>
                  {targetSlug === cat.slug && <Check className="w-4 h-4 text-blue-400" />}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={onClose} className="flex-1 h-10 bg-white/[0.06] border border-white/10 text-gray-400 rounded-xl text-sm font-semibold active:bg-white/10">Abbrechen</button>
            <button onClick={handleBulkMove} disabled={!targetSlug || moving}
              className="flex-1 h-10 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
              data-testid="vault-bulk-move-btn"
            >
              {moving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Verschieben & Loeschen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════
// CREATE / EDIT FORM
// ═══════════════════════════════════════════
function VaultForm({ editEntry, presetProject, categories, onClose, onSaved }) {
  const [title, setTitle] = useState(editEntry?.title || "");
  const [entryType, setEntryType] = useState(editEntry?.entry_type || "web_login");
  const [username, setUsername] = useState(editEntry?.username || "");
  const [password, setPassword] = useState("");
  const [url, setUrl] = useState(editEntry?.url || "");
  const [description, setDescription] = useState(editEntry?.description || "");
  const defaultCat = categories.find(c => c.is_default);
  const [category, setCategory] = useState(editEntry?.category || defaultCat?.slug || "sonstiges");
  const [accessType, setAccessType] = useState(editEntry?.access_type || "admin_only");
  const [projectId, setProjectId] = useState(editEntry?.project_id || presetProject?.id || "");
  const [projects, setProjects] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const isEdit = !!editEntry;

  // Email-account specific fields
  const [emailAddress, setEmailAddress] = useState(editEntry?.email_address || "");
  const [displayName, setDisplayName] = useState(editEntry?.display_name || "");
  const [imapHost, setImapHost] = useState(editEntry?.imap_host || "imap.strato.de");
  const [imapPort, setImapPort] = useState(editEntry?.imap_port || 993);
  const [smtpHost, setSmtpHost] = useState(editEntry?.smtp_host || "smtp.strato.de");
  const [smtpPort, setSmtpPort] = useState(editEntry?.smtp_port || 465);
  const [isGlobal, setIsGlobal] = useState(editEntry?.is_global ?? true);
  const [isActive, setIsActive] = useState(editEntry?.is_active ?? true);

  useEffect(() => {
    authFetch(`${API}/projects`).then(r => r.ok ? r.json() : []).then(setProjects).catch(() => {});
    if (isEdit) {
      authFetch(`${API}/vault/${editEntry.id}/reveal`, { method: "POST" })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setPassword(d.password || ""); })
        .catch(() => {});
    }
  }, [isEdit, editEntry]);

  const handleSave = async () => {
    if (!title.trim()) { toast.error("Titel erforderlich"); return; }
    setSaving(true);
    const body = {
      title: title.trim(),
      entry_type: entryType,
      username: username.trim(),
      password,
      url: url.trim(),
      description: description.trim(),
      category,
      access_type: accessType,
      project_id: projectId || null,
    };
    if (entryType === "email_account") {
      // Pre-save validation — keeps the backend error messages from surprising the user
      if (!emailAddress.trim()) { toast.error("E-Mail-Adresse erforderlich"); setSaving(false); return; }
      if (!imapHost.trim() || !imapPort) { toast.error("IMAP-Server + Port erforderlich"); setSaving(false); return; }
      if (!smtpHost.trim() || !smtpPort) { toast.error("SMTP-Server + Port erforderlich"); setSaving(false); return; }
      Object.assign(body, {
        email_address: emailAddress.trim().toLowerCase(),
        display_name: displayName.trim(),
        imap_host: imapHost.trim(), imap_port: Number(imapPort), imap_security: "SSL_TLS",
        smtp_host: smtpHost.trim(), smtp_port: Number(smtpPort), smtp_security: "SSL_TLS",
        is_global: isGlobal,
        is_active: isActive,
      });
    }
    try {
      const res = isEdit
        ? await authFetch(`${API}/vault/${editEntry.id}`, { method: "PUT", body: JSON.stringify(body) })
        : await authFetch(`${API}/vault`, { method: "POST", body: JSON.stringify(body) });
      if (res.ok) { toast.success(isEdit ? "Gespeichert" : "Eintrag erstellt"); onSaved(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Netzwerkfehler"); }
    setSaving(false);
  };

  const isEmailAccount = entryType === "email_account";

  return (
    <div className="fixed inset-0 z-[800] flex items-end justify-center" data-testid="vault-form">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#1a1d27] border-t border-white/10 rounded-t-2xl max-h-[85vh] overflow-y-auto pb-safe">
        <div className="sticky top-0 bg-[#1a1d27] z-10 px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h2 className="text-base font-bold text-white">{isEdit ? "Eintrag bearbeiten" : "Neuer Eintrag"}</h2>
          <button onClick={onClose} className="p-2 text-gray-400 active:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Titel *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. DPD Lehrte WLAN"
              className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50"
              data-testid="vault-form-title" />
          </div>

          {/* Dynamic Categories */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Kategorie</label>
            <div className="flex gap-2 flex-wrap">
              {categories.map(cat => {
                const CIcon = getIconComponent(cat.icon);
                return (
                  <button key={cat.slug} onClick={() => setCategory(cat.slug)}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${category === cat.slug ? "bg-white/15 text-white border border-white/20" : "bg-white/[0.04] text-gray-500 border border-transparent active:bg-white/8"}`}
                    data-testid={`vault-form-cat-${cat.slug}`}
                  ><CIcon className="w-3 h-3" />{cat.name}</button>
                );
              })}
            </div>
          </div>

          {/* Entry-Type selector — web_login (Standard) oder Email-Account */}
          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Eintragstyp</label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setEntryType("web_login")}
                className={`px-3 py-2.5 rounded-xl text-xs font-semibold border transition-colors ${!isEmailAccount ? "bg-white/15 border-white/25 text-white" : "bg-white/[0.04] border-transparent text-gray-500"}`}
                data-testid="vault-form-type-login"
              >
                Website-Login
              </button>
              <button onClick={() => setEntryType("email_account")}
                className={`px-3 py-2.5 rounded-xl text-xs font-semibold border transition-colors ${isEmailAccount ? "bg-blue-500/20 border-blue-500/40 text-white" : "bg-white/[0.04] border-transparent text-gray-500"}`}
                data-testid="vault-form-type-email"
              >
                E-Mail-Konto
              </button>
            </div>
            {isEmailAccount && (
              <p className="text-[10px] text-gray-500 mt-1.5">Zusätzliche Pflichtfelder für IMAP/SMTP werden unten eingeblendet. Nur vollständige Einträge erscheinen im Email-Account-Dropdown.</p>
            )}
          </div>

          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Benutzername</label>
            <input value={username} onChange={e => setUsername(e.target.value)} placeholder="admin, user@email.de..."
              className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50"
              data-testid="vault-form-username" />
          </div>

          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Passwort</label>
            <div className="relative">
              <input type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                placeholder={isEdit ? "Leer lassen = unveraendert" : "Passwort eingeben"}
                className="w-full h-11 px-4 pr-12 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50 font-mono"
                data-testid="vault-form-password" />
              <button onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">URL (optional)</label>
            <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://..."
              className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50"
              data-testid="vault-form-url" />
          </div>

          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Beschreibung (optional)</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Notizen..." rows={2}
              className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50 resize-none"
              data-testid="vault-form-desc" />
          </div>

          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Projekt (optional)</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)}
              className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm appearance-none outline-none focus:border-blue-500/50"
              data-testid="vault-form-project">
              <option value="">Kein Projekt</option>
              {projects.filter(p => p.status === "aktiv").map(p => (
                <option key={p.id} value={p.id}>{p.display_name || p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Zugriff</label>
            <div className="space-y-1.5">
              {ACCESS_OPTIONS.map(opt => (
                <button key={opt.id} onClick={() => setAccessType(opt.id)}
                  className={`w-full px-4 py-3 rounded-xl text-sm text-left transition-colors ${accessType === opt.id ? "bg-blue-500/15 border border-blue-500/30 text-white font-semibold" : "bg-white/[0.04] border border-white/8 text-gray-400"}`}
                  data-testid={`vault-form-access-${opt.id}`}
                >
                  <div className="flex items-center justify-between">{opt.label}{accessType === opt.id && <Check className="w-4 h-4 text-blue-400" />}</div>
                </button>
              ))}
            </div>
          </div>

          {/* ── Email-Account-specific fields ── */}
          {isEmailAccount && (
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-3 space-y-3" data-testid="vault-form-email-section">
              <p className="text-[11px] font-bold uppercase tracking-wider text-blue-300">E-Mail-Konto Details</p>

              <div>
                <label className="text-xs text-gray-400 font-semibold mb-1.5 block">E-Mail-Adresse *</label>
                <input value={emailAddress} onChange={e => setEmailAddress(e.target.value)} placeholder="name@tgaone.de"
                  className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50"
                  data-testid="vault-form-email-address" />
              </div>

              <div>
                <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Anzeige-Name</label>
                <input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Ronny Pforr | TGAone"
                  className="w-full h-11 px-4 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm placeholder:text-gray-600 outline-none focus:border-blue-500/50"
                  data-testid="vault-form-display-name" />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-xs text-gray-400 font-semibold mb-1.5 block">IMAP-Server *</label>
                  <input value={imapHost} onChange={e => setImapHost(e.target.value)} placeholder="imap.strato.de"
                    className="w-full h-11 px-3 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm outline-none focus:border-blue-500/50 font-mono"
                    data-testid="vault-form-imap-host" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Port *</label>
                  <input type="number" value={imapPort} onChange={e => setImapPort(e.target.value)}
                    className="w-full h-11 px-3 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm outline-none focus:border-blue-500/50 font-mono"
                    data-testid="vault-form-imap-port" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="text-xs text-gray-400 font-semibold mb-1.5 block">SMTP-Server *</label>
                  <input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.strato.de"
                    className="w-full h-11 px-3 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm outline-none focus:border-blue-500/50 font-mono"
                    data-testid="vault-form-smtp-host" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 font-semibold mb-1.5 block">Port *</label>
                  <input type="number" value={smtpPort} onChange={e => setSmtpPort(e.target.value)}
                    className="w-full h-11 px-3 bg-white/[0.06] border border-white/10 rounded-xl text-white text-sm outline-none focus:border-blue-500/50 font-mono"
                    data-testid="vault-form-smtp-port" />
                </div>
              </div>

              <div className="flex gap-4 pt-1">
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={isGlobal} onChange={e => setIsGlobal(e.target.checked)} data-testid="vault-form-is-global" />
                  Global (für alle Admins sichtbar)
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} data-testid="vault-form-is-active" />
                  Aktiv
                </label>
              </div>
            </div>
          )}

          <button onClick={handleSave} disabled={saving || !title.trim()}
            className="w-full h-12 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
            data-testid="vault-form-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {isEdit ? "Speichern" : "Eintrag erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}
